import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Space,
  Card,
  Select,
  Skeleton,
  Collapse,
  Segmented,
  Switch,
  DatePicker,
  InputNumber,
  Tag,
  Checkbox,
} from 'antd';
import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { getAPICall } from '../../api';
import { CustomFormSelector } from '../CustomSelector';
import { getAll as getCountries } from '../../api/module/countries';
import { getByCountry } from '../../api/module/states';
import {
  getByState,
  getByCountry as getCitiesByCountry,
} from '../../api/module/cities';
import qs from 'qs';
import dayjs from 'dayjs';
import { useHistory, useLocation } from 'react-router-dom';
import './style.scss';

/**
 * `AdvancedPersonSearch` handles all the formatting required for the backend to filter according to people data.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.title] - Label of the collapse item that wraps the form
 * @param {string} [props.infoEndpoint] - API Endpoint URL to request the select options cache
 * @param {function(DynamicFormValues): void} [props.onFinish] - Callback triggered immediately upon valid form submission or successful query parsing.
 *
 * @example
 * const [initialValues, setInitialValues] = useState(false);
 * useEffect(()=>setInitialValues(qs.parse()),[]);
 * const handleSave = async (data) => console.log('Saving values...', data);
 * <AdvancedPerson initialValues={initialValues} onFinish={handleSave} />
 */

const SectionDivider = ({ operator }) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        margin: '16px 0 16px 0',
      }}
    >
      <span
        style={{
          color: '#276749',
          backgroundColor: '#f0fff4',
          padding: '6px 14px',
          borderRadius: '10px',
          fontSize: '12px',
          fontWeight: '600',
          letterSpacing: '0.5px',
          border: '1px solid #c6f6d5',
        }}
      >
        {operator === 'or' ? 'O' : 'Y'}
      </span>
    </div>
  );
};

const AdvancedPersonSearch = ({
  title = 'Búsqueda de perfiles',
  infoEndpoint = undefined,
  onFinish = () => {},
}) => {
  const navigate = useHistory();
  const location = useLocation();
  const [searchInfo, setSearchInfo] = useState();
  const [initialValues, setInitialValues] = useState(false);
  const [countryLabel, setCountryLabel] = useState('');
  const [deparmentLabel, setDeparmentLabel] = useState('');
  const [cityLabel, setCityLabel] = useState('');
  const [form] = Form.useForm();

  const sectionOperatorA = Form.useWatch([0, 'operator'], form);
  const sectionOperatorB = Form.useWatch([1, 'operator'], form);

  const sectionListA = Form.useWatch([0, 'conditions'], form);
  const sectionListB = Form.useWatch([1, 'conditions'], form);

  const selectedCountry = Form.useWatch([2, 'conditions', 'pais'], form);
  const selectedDepartment = Form.useWatch([2, 'conditions', 'estado'], form);

  const selectedLocation = Form.useWatch([2, 'conditions'], form);

  const fetchInfo = async () => {
    const { data: resData } = await getAPICall(infoEndpoint);
    function listToMap(pv, cv) {
      pv[cv.value] = cv.label;
      return pv;
    }
    setSearchInfo({
      ...resData,
      nivelMap: resData.nivel.reduce(listToMap, {}),
      areaMap: resData.area.reduce(listToMap, {}),
      areaExMap: resData.area_ex.reduce(listToMap, {}),
      tipoMap: resData.tipo_cargo.reduce(listToMap, {}),
    });
  };

  useEffect(() => {
    const q = location.search;
    if (q) {
      const parsedFilters = qs.parse(q, {
        ignoreQueryPrefix: true,
        depth: 4,
        decoder(str, defaultDecoder, charset, type) {
          if (type === 'value') {
            if (str === 'true') return true;
            if (str === 'false') return false;

            // Need numeric strings to convert to real numbers
            if (/^\d+$/.test(str)) return parseInt(str, 10);
          }

          // Fall back to the default qs decoder for all other strings/keys
          return defaultDecoder(str);
        },
      });
      fetchInfo();
      setInitialValues(parsedFilters);
      form.submit();
    } else
      setInitialValues({
        0: { operator: 'and' },
        1: { operator: 'and' },
      });
  }, []);

  function filterOption(input, option) {
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  }

  function disabledDate(current) {
    return current && current > dayjs().endOf('day');
  }

  const onSelfFinish = (values) => {
    if (!values[0]?.conditions || values[0]?.conditions?.length === 0) {
      delete values[0];
    } else {
      if (!values[0].operator) values[0].operator = 'and';
    }
    if (!values[1]?.conditions || values[1]?.conditions?.length === 0) {
      delete values[1];
    } else {
      if (!values[1].operator) values[1].operator = 'and';
    }
    if (values[2] && !!values[2]?.conditions.pais) {
      values[2].operator = 'and';
    } else {
      delete values[2];
    }
    if (!values[3]?.conditions.tiene_contrato) {
      delete values[3];
    }
    const searchString = qs.stringify(values, {
      skipNulls: true,
      encode: true,
      depth: 4,
    });
    navigate.push({
      pathname: location.pathname,
      search: `?${searchString}`,
    });
    onFinish(searchString);
  };

  if (!initialValues) return <Skeleton loading active paragraph />;

  return (
    <Collapse
      defaultActiveKey={location.search ? '1' : undefined}
      onChange={(key) => key.length > 0 && fetchInfo()}
      items={[
        {
          key: '1',
          label: title,
          children: searchInfo ? (
            <>
              <b>
                Combine criterios de educación, experiencia y ubicación. Cada
                bloque tiene su propio operador (Y/O); entre bloques siempre se
                aplica Y.
              </b>
              <Form
                form={form}
                name="advanced_search"
                layout="vertical"
                onFinish={onSelfFinish}
                autoComplete="off"
                initialValues={initialValues}
              >
                <Collapse
                  items={[
                    {
                      key: 'educacion',
                      label: 'Educación',
                      children: (
                        <>
                          <Form.Item
                            name={[0, 'operator']}
                            label="La persona debe cumplir"
                            layout="horizontal"
                          >
                            <Segmented
                              options={[
                                { value: 'and', label: 'Todas (y)' },
                                { value: 'or', label: 'Al menos una (o)' },
                              ]}
                            />
                          </Form.Item>
                          <Form.List name={[0, 'conditions']}>
                            {(fields, { add, remove }) => (
                              <>
                                {fields.map(
                                  ({ key, name, ...restField }, idx) => (
                                    <div key={key}>
                                      {idx > 0 && (
                                        <Form.Item noStyle>
                                          <SectionDivider
                                            operator={sectionOperatorA}
                                          />
                                        </Form.Item>
                                      )}
                                      <Card
                                        key={key}
                                        size="small"
                                        style={{
                                          backgroundColor: '#fafafa',
                                        }}
                                        title={`${idx + 1}. Condición Educación`}
                                        extra={
                                          <Space align="baseline">
                                            <Form.Item
                                              noStyle
                                              name={[name, 'exclude']}
                                              valuePropName="checked"
                                            >
                                              <Switch
                                                checkedChildren="Excluido"
                                                unCheckedChildren="Incluido"
                                                className="red-switch"
                                              />
                                            </Form.Item>
                                            <DeleteFilled
                                              style={{
                                                color: '#ff4d4f',
                                                fontSize: 18,
                                              }}
                                              onClick={() => remove(name)}
                                            />
                                          </Space>
                                        }
                                      >
                                        <Space
                                          style={{
                                            width: '100%',
                                          }}
                                          align="baseline"
                                          direction="horizontal"
                                        >
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'nivel']}
                                            label="Nivel educativo"
                                            rules={[
                                              {
                                                required: true,
                                              },
                                            ]}
                                          >
                                            <Select
                                              style={{ minWidth: '12em' }}
                                              options={searchInfo.nivel}
                                              filterOption={filterOption}
                                              placeholder="Seleccione..."
                                              allowClear
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'area']}
                                            label="Área del conocimiento"
                                          >
                                            <Select
                                              style={{ minWidth: '20em' }}
                                              options={searchInfo.area}
                                              filterOption={filterOption}
                                              placeholder="Seleccione..."
                                              allowClear
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'titulo']}
                                            label="Título contiene"
                                          >
                                            <Input
                                              placeholder="Nombre..."
                                              allowClear
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'estado']}
                                            label="Estado del título"
                                          >
                                            <Select
                                              style={{ minWidth: '8em' }}
                                              options={[
                                                {
                                                  value: false,
                                                  label: 'En curso',
                                                },
                                                {
                                                  value: true,
                                                  label: 'Finalizado',
                                                },
                                              ]}
                                              placeholder="Seleccione..."
                                              allowClear
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'fecha']}
                                            label="Fecha de finalización"
                                            getValueProps={(value) =>
                                              value
                                                ? dayjs(value, 'YYYY-MM-DD')
                                                : null
                                            }
                                            getValueFromEvent={(
                                              _date,
                                              dateString,
                                            ) => dateString}
                                          >
                                            <DatePicker
                                              disabledDate={disabledDate}
                                              format="YYYY-MM-DD"
                                              allowClear
                                            />
                                          </Form.Item>
                                        </Space>
                                      </Card>
                                    </div>
                                  ),
                                )}
                                <Space style={{ marginTop: 10 }}>
                                  {fields.length < 5 && (
                                    <Form.Item>
                                      <Button
                                        type="default"
                                        onClick={() => add()}
                                        icon={<PlusOutlined />}
                                      >
                                        Agregar condición
                                      </Button>
                                    </Form.Item>
                                  )}
                                  <Form.Item>
                                    <Button
                                      type="default"
                                      htmlType="button"
                                      danger
                                      onClick={() =>
                                        form.setFieldsValue({
                                          0: {
                                            operator: 'and',
                                            conditions: [],
                                          },
                                        })
                                      }
                                    >
                                      Limpiar
                                    </Button>
                                  </Form.Item>
                                </Space>
                              </>
                            )}
                          </Form.List>
                        </>
                      ),
                      extra: `${sectionListA?.length || 0} ${sectionListA?.length === 1 ? 'condición' : 'condiciones'} - ${sectionOperatorA === 'or' ? 'O' : 'Y'}`,
                    },
                    {
                      key: 'experiencia',
                      label: 'Experiencia',
                      children: (
                        <>
                          <Form.Item
                            name={[1, 'operator']}
                            label="La persona debe cumplir"
                            layout="horizontal"
                          >
                            <Segmented
                              options={[
                                { value: 'and', label: 'Todas (y)' },
                                { value: 'or', label: 'Al menos una (o)' },
                              ]}
                            />
                          </Form.Item>
                          <Form.List name={[1, 'conditions']}>
                            {(fields, { add, remove }) => (
                              <>
                                {fields.map(
                                  ({ key, name, ...restField }, idx) => (
                                    <div key={key}>
                                      {idx > 0 && (
                                        <Form.Item noStyle>
                                          <SectionDivider
                                            operator={sectionOperatorB}
                                          />
                                        </Form.Item>
                                      )}
                                      <Card
                                        key={key}
                                        size="small"
                                        style={{
                                          backgroundColor: '#fafafa',
                                        }}
                                        title={`${idx + 1}. Condición Experiencia`}
                                        extra={
                                          <Space align="baseline">
                                            <Form.Item
                                              noStyle
                                              name={[name, 'exclude']}
                                              valuePropName="checked"
                                            >
                                              <Switch
                                                checkedChildren="Excluido"
                                                unCheckedChildren="Incluido"
                                                className="red-switch"
                                              />
                                            </Form.Item>
                                            <DeleteFilled
                                              style={{
                                                color: '#ff4d4f',
                                                fontSize: 18,
                                              }}
                                              onClick={() => remove(name)}
                                            />
                                          </Space>
                                        }
                                      >
                                        <Space
                                          style={{
                                            width: '100%',
                                          }}
                                          align="baseline"
                                          direction="horizontal"
                                        >
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'cargo']}
                                            label="Cargo"
                                            rules={[{ required: true }]}
                                          >
                                            <Input
                                              placeholder="Nombre..."
                                              allowClear
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'estado']}
                                            label="Estado de la experiencia"
                                          >
                                            <Select
                                              style={{ minWidth: '12em' }}
                                              options={[
                                                {
                                                  value: false,
                                                  label: 'Trabajo actual',
                                                },
                                                {
                                                  value: true,
                                                  label: 'Trabajo finalizado',
                                                },
                                              ]}
                                              placeholder="Seleccione..."
                                              allowClear
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'area']}
                                            label="Área de experiencia"
                                          >
                                            <Select
                                              style={{ minWidth: '20em' }}
                                              options={searchInfo.area_ex}
                                              filterOption={filterOption}
                                              placeholder="Seleccione..."
                                              allowClear
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'meses']}
                                            label="Meses de experiencia"
                                          >
                                            <InputNumber
                                              placeholder="Meses..."
                                              style={{ width: '100%' }}
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'tipo']}
                                            label="Tipo de cargo"
                                          >
                                            <Select
                                              style={{ minWidth: '12em' }}
                                              options={searchInfo.tipo_cargo}
                                              filterOption={filterOption}
                                              placeholder="Seleccione..."
                                              allowClear
                                            />
                                          </Form.Item>
                                        </Space>
                                      </Card>
                                    </div>
                                  ),
                                )}
                                <Space style={{ marginTop: 10 }}>
                                  {fields.length < 5 && (
                                    <Form.Item>
                                      <Button
                                        type="default"
                                        onClick={() => add()}
                                        icon={<PlusOutlined />}
                                      >
                                        Agregar condición
                                      </Button>
                                    </Form.Item>
                                  )}
                                  <Form.Item>
                                    <Button
                                      type="default"
                                      htmlType="button"
                                      danger
                                      onClick={() =>
                                        form.setFieldsValue({
                                          1: {
                                            operator: 'and',
                                            conditions: [],
                                          },
                                        })
                                      }
                                    >
                                      Limpiar
                                    </Button>
                                  </Form.Item>
                                </Space>
                              </>
                            )}
                          </Form.List>
                        </>
                      ),

                      extra: `${sectionListB?.length || 0} ${sectionListB?.length === 1 ? 'condición' : 'condiciones'} - ${sectionOperatorB === 'or' ? 'O' : 'Y'}`,
                    },
                    {
                      key: 'lugar',
                      label: 'Lugar de residencia',
                      children: (
                        <Card
                          key={'lugar'}
                          size="small"
                          style={{
                            backgroundColor: '#fafafa',
                          }}
                          title="Condición Lugar de residencia"
                          extra={
                            <Space align="baseline">
                              <Form.Item
                                noStyle
                                name={[2, 'conditions', 'exclude']}
                                valuePropName="checked"
                              >
                                <Switch
                                  checkedChildren="Excluido"
                                  unCheckedChildren="Incluido"
                                  className="red-switch"
                                />
                              </Form.Item>
                              <DeleteFilled
                                style={{
                                  color: '#ff4d4f',
                                  fontSize: 18,
                                }}
                                onClick={() =>
                                  form.setFieldsValue({
                                    2: {
                                      conditions: {
                                        pais: undefined,
                                        estado: undefined,
                                        ciudad: undefined,
                                      },
                                    },
                                  })
                                }
                              />
                            </Space>
                          }
                        >
                          <Space>
                            <Form.Item
                              label="País de notificación"
                              name={[2, 'conditions', 'pais']}
                            >
                              <CustomFormSelector
                                sendLabel={setCountryLabel}
                                endpointFetch={getCountries}
                                valueName="id"
                                labelName="nombre"
                                selectorProps={{ style: { minWidth: '15rem' } }}
                                onChange={() =>
                                  form.setFieldsValue({
                                    2: {
                                      conditions: {
                                        estado: undefined,
                                        ciudad: undefined,
                                      },
                                    },
                                  })
                                }
                              />
                            </Form.Item>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, curr) =>
                                prev[2]?.conditions?.pais !==
                                curr[2]?.conditions?.pais
                              }
                            >
                              {() =>
                                selectedCountry === 45 || !selectedCountry ? (
                                  <Space>
                                    <Form.Item
                                      label="Departamento de notificación"
                                      name={[2, 'conditions', 'estado']}
                                    >
                                      {selectedCountry ? (
                                        <CustomFormSelector
                                          sendLabel={setDeparmentLabel}
                                          endpointFetch={async () =>
                                            await getByCountry(selectedCountry)
                                          }
                                          valueName="id"
                                          labelName="nombre"
                                          selectorKey={selectedCountry}
                                          selectorProps={{
                                            style: { minWidth: '15rem' },
                                          }}
                                          onChange={() =>
                                            form.setFieldsValue({
                                              2: {
                                                conditions: {
                                                  ciudad: undefined,
                                                },
                                              },
                                            })
                                          }
                                        />
                                      ) : (
                                        <Select
                                          disabled
                                          style={{ minWidth: '15rem' }}
                                        />
                                      )}
                                    </Form.Item>
                                    <Form.Item
                                      label="Ciudad de notificación"
                                      name={[2, 'conditions', 'ciudad']}
                                    >
                                      {selectedDepartment ? (
                                        <CustomFormSelector
                                          sendLabel={setCityLabel}
                                          mode="multiple"
                                          endpointFetch={async () =>
                                            await getByState(selectedDepartment)
                                          }
                                          valueName="id"
                                          labelName="nombre"
                                          selectorKey={selectedDepartment}
                                          selectorProps={{
                                            style: { minWidth: '15rem' },
                                          }}
                                        />
                                      ) : (
                                        <Select
                                          disabled
                                          style={{ minWidth: '15rem' }}
                                        />
                                      )}
                                    </Form.Item>
                                  </Space>
                                ) : (
                                  <Form.Item
                                    label="Ciudad de notificación"
                                    name={[2, 'conditions', 'ciudad']}
                                  >
                                    {selectedCountry ? (
                                      <CustomFormSelector
                                        sendLabel={setCityLabel}
                                        mode="multiple"
                                        endpointFetch={async () =>
                                          await getCitiesByCountry(
                                            selectedCountry,
                                          )
                                        }
                                        valueName="id"
                                        labelName="nombre"
                                        selectorKey={selectedCountry}
                                        selectorProps={{
                                          style: { minWidth: '15rem' },
                                        }}
                                      />
                                    ) : (
                                      <Select
                                        disabled
                                        style={{ minWidth: '15rem' }}
                                      />
                                    )}
                                  </Form.Item>
                                )
                              }
                            </Form.Item>
                          </Space>
                        </Card>
                      ),
                    },
                  ]}
                  defaultActiveKey={[
                    initialValues[0]?.conditions?.length > 0 ? 'educacion' : 0,
                    initialValues[1]?.conditions?.length > 0
                      ? 'experiencia'
                      : 0,
                    !!initialValues[2]?.conditions?.pais ? 'lugar' : 0,
                  ].filter((k) => k !== 0)}
                />
                <div
                  style={{
                    backgroundColor: '#fafafa',
                    margin: '15px 0 15px 0',
                    borderRadius: '10px',
                    padding: '1em',
                  }}
                >
                  <p
                    style={{
                      marginBottom: 0,
                    }}
                  >
                    <span
                      style={{
                        color: 'gray',
                        fontWeight: 'bold',
                      }}
                    >
                      VISTA PREVIA:{' '}
                    </span>
                    Personas con{' '}
                    {sectionListA?.length > 0 ? (
                      <span className="preview-tag">
                        <span className="tag-title">Educación: </span>
                        {sectionListA.map((v, idx) => (
                          <span key={idx}>
                            {idx > 0 && (
                              <span className="tag-operator">
                                {' '}
                                {sectionOperatorA === 'or' ? 'O' : 'Y'}{' '}
                              </span>
                            )}
                            <Tag
                              className="tag"
                              color={v?.exclude ? 'red' : 'green'}
                            >
                              {v?.exclude ? 'sin ' : ''}
                              {searchInfo.nivelMap[v?.nivel]}
                              {v?.area
                                ? ` en ${searchInfo.areaMap[v.area]}`
                                : ''}
                              {v?.titulo
                                ? ` con <<${v.titulo}>> en el título`
                                : ''}
                              {v?.estado != null
                                ? ` y estado ${v.estado ? 'finalizado' : 'en curso'}`
                                : ''}
                              {v?.fecha
                                ? ` y fecha de finalización desde <<${v.fecha}>>`
                                : ''}
                            </Tag>
                          </span>
                        ))}
                      </span>
                    ) : (
                      ''
                    )}
                    {sectionListB?.length > 0 ? (
                      <>
                        {sectionListA?.length > 0 ? (
                          <span className="preview-tag ">
                            <span className="tag-operator"> Y </span>
                          </span>
                        ) : (
                          ''
                        )}
                        <span className="preview-tag">
                          <span className="tag-title">Experiencia: </span>
                          {sectionListB.map((v, idx) => (
                            <span key={idx}>
                              {idx > 0 && (
                                <span className="tag-operator">
                                  {' '}
                                  {sectionOperatorB === 'or' ? 'O' : 'Y'}{' '}
                                </span>
                              )}
                              <Tag
                                className="tag"
                                color={v?.exclude ? 'red' : 'green'}
                              >
                                {v?.exclude ? 'sin ' : ''}
                                {[
                                  v?.meses
                                    ? `experiencia ≥ ${v.meses} meses`
                                    : null,
                                  v?.area
                                    ? `en ${searchInfo.areaExMap[v.area]}`
                                    : null,
                                  v?.tipo
                                    ? `(${searchInfo.tipoMap[v.tipo]})`
                                    : null,
                                  v?.cargo ? `cargo <<${v.cargo}>>` : null,
                                  v?.estado != null
                                    ? `con estado ${v.estado ? 'finalizado' : 'actual'}`
                                    : null,
                                ]
                                  .filter((t) => t != null)
                                  .join(', ')}
                              </Tag>
                            </span>
                          ))}
                        </span>
                      </>
                    ) : (
                      ''
                    )}
                    {selectedCountry ? (
                      <>
                        {sectionListA?.length > 0 ||
                        sectionListB?.length > 0 ? (
                          <span className="preview-tag ">
                            <span className="tag-operator"> Y </span>
                          </span>
                        ) : (
                          ''
                        )}
                        <span className="preview-tag">
                          <span className="tag-title">Ubicación: </span>
                          <Tag
                            className="tag"
                            color={selectedLocation?.exclude ? 'red' : 'green'}
                          >
                            {selectedLocation?.exclude ? 'no ' : ''}vive en{' '}
                            {countryLabel}
                            {selectedDepartment ? ` / ${deparmentLabel}` : ''}
                            {cityLabel ? ` / (${cityLabel})` : ''}
                          </Tag>
                        </span>
                      </>
                    ) : (
                      ''
                    )}
                  </p>
                </div>
                <Form.Item
                  name={[3, 'conditions', 'tiene_contrato']}
                  valuePropName="checked"
                >
                  <Checkbox>
                    Buscar solo sobre las personas que hayan tenido contrato con
                    Corpoeducación
                  </Checkbox>
                </Form.Item>
                <Space>
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      Buscar
                    </Button>
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="default"
                      htmlType="button"
                      danger
                      onClick={() =>
                        form.setFieldsValue({
                          0: { operator: 'and', conditions: [] },
                          1: { operator: 'and', conditions: [] },
                          2: {
                            conditions: {
                              exclude: undefined,
                              pais: undefined,
                              estado: undefined,
                              ciudad: undefined,
                            },
                          },
                          3: {
                            conditions: {
                              tiene_contrato: false,
                            },
                          },
                        })
                      }
                    >
                      Borrar filtros
                    </Button>
                  </Form.Item>
                </Space>
              </Form>
            </>
          ) : (
            <Skeleton loading active paragraph />
          ),
        },
      ]}
    />
  );
};

export default AdvancedPersonSearch;
